import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, NgZone, OnDestroy, OnInit, Output, WritableSignal, computed, effect, inject, input, signal } from '@angular/core';
import { MapSharedUtilService } from '../../services/map-shared-util.service';
import maplibregl, { Map as MaplibreMap, MapGeoJSONFeature, VectorTileSource } from 'maplibre-gl';
import { OConfigService } from '@olameter/o-config-service';
import { ColumnSettings } from '../../../CommonFiles/UIModels';
import { ReadyForDispatchColor } from '../../../CommonFiles/enums/workOrderDetails.enum';
import { WOStatusWeb } from '../../../constants/woStatusOptions';
import { WorkorderGRPCService } from '../../../ProxyServices/workorder.grpc.service';
import { Subscription } from 'rxjs';

import { WorkOrderBase } from '../../../CommonFiles/BusinessModels';
import { AppComponent } from '../../../app.component';
import { Feature, Position } from 'geojson';


import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import { ONotificationService } from '@olameter/o-utilities';
import { SpiderfyService } from 'src/app/home/services/spiderfy.service';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { TranslateService } from '@ngx-translate/core';
@Component({
  standalone: false,
  selector: 'app-workorders-map',
  templateUrl: './workorders-map.component.html',
  styleUrls: ['./workorders-map.component.scss'],
  providers: [SpiderfyService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkOrdersMapComponent implements OnInit, OnDestroy {
  spiderfyService = inject(SpiderfyService);
  private readonly additionalLayerTranslationKeys: Record<string, string> = {
    CGR: 'HOME_COMPONENTS.WORKORDERS_MAP.ADDITIONAL_LAYERS.CGR',
    '3DBuildings': 'HOME_COMPONENTS.WORKORDERS_MAP.ADDITIONAL_LAYERS.BUILDINGS_3D',
    TechnicianAddress:
      'HOME_COMPONENTS.WORKORDERS_MAP.ADDITIONAL_LAYERS.TECHNICIAN_ADDRESS',
    Spiderify: 'HOME_COMPONENTS.WORKORDERS_MAP.ADDITIONAL_LAYERS.SPIDERIFY',
    'Satellite Imagery':
      'HOME_COMPONENTS.WORKORDERS_MAP.ADDITIONAL_LAYERS.SATELLITE_IMAGERY',
    'Terrain Overlays':
      'HOME_COMPONENTS.WORKORDERS_MAP.ADDITIONAL_LAYERS.TERRAIN_OVERLAYS',
  };
 // grid state for kendo grid
  // reset this initiate
  gridState = input<State>();
  gridStateMap = input<State>();
  columnsList = input<ColumnSettings[]>([]);
  contractName = input<string>();
  startDateFilter = input<Date>();
  endDateFilter = input<Date>();
  installDateOption = input<string>();
  workOrders = input<WorkOrderBase[]>([]);
  gridData = input<GridDataResult>({
    total: 0,
    data: []
  });

  queryId = computed<number | null>(() => {
    const workorders = this.workOrders();
    return workorders[0]?.queryId ?? null;
  });

  workOrdersMap = computed<Map<string, WorkOrderBase>>(() => {
    return new Map(this.workOrders().map(item => [item.name, item]))
  });
 
  selectedWorkOrders = input<WorkOrderBase[]>([]);
  selectMapTool =  input<string>();
  @Output() updateWOSelection = new EventEmitter<WorkOrderBase[]>();

  // loading state of map
  loadingMap:WritableSignal<boolean> = signal(true);
  // map instance 
  mapInstance: WritableSignal<MaplibreMap> = signal(null);
  // flag to show loading state when work orders selection is processing
  pendingMapSelection:WritableSignal<boolean> = signal(false);
  // track source change, to trigger loadLegend method.
  trackMapSourceChange:WritableSignal<boolean> = signal(false);
  //turn Spiderify Effect On/Off
  spiderifyEffect:WritableSignal<boolean> = signal(false);

  readonly defaultZoom = MapSharedUtilService.defaultZoom;
  readonly defaultCenter = MapSharedUtilService.defaultCenter;
  readonly pointSource = 'work_order';
  //TODO - Commented Hexagon Layer due to performance
  //readonly hexagonSource = 'work_order_hexagon';
  readonly terrainDataSquare = 'terrain-data-square';
  readonly layerName: string; //this is the name of the DB function or materialized view to use
  mappopupRef: maplibregl.Popup;
  //TODO uncomment on future spiderifier: MaplibreglSpiderifier;
  public availableHeight: number = AppComponent.availableHeight; // ??
  fsrNames4LegendC: Array<object>[] = [];
  teams4LegendC: Array<object>[] = [];
  selectedWOIDs: WorkOrderBase[] = [];
  openMapTileUrl: string;
  polygonFeature: Array<Feature>;
  prevpolygonFeature: Array<Feature>;
  private subscriptions: Subscription[] = [];
  isClickAllowed = true; //Debouncing with a Flag
  //unselect feature when shift key is pressed
  public isShiftKeyPressed:WritableSignal<boolean> = signal(false);
  randomTileID: number;

  private sourcedataBound: any;
  private idleEventBound: any;
  private styleDataEventBound: any;
  private fullscreenChangeHandler: (() => void) | null = null;

  constructor(
    private _config: OConfigService,
    public _mapSharedUtilService: MapSharedUtilService,
    private _clientWO: WorkorderGRPCService,
    private zone: NgZone,
    private notifications: ONotificationService,
    private ref: ChangeDetectorRef,
    private translate: TranslateService
  ) {
    this.layerName = 'default';
    this.openMapTileUrl = MapSharedUtilService.openMapTileUrl;
    this.availableHeight = AppComponent.availableHeight;
    this.randomTileID = Math.random();

    effect(() => {
      const newSelectedWOs = [...this.selectedWorkOrders()];
      // update pointers on map
      const thisMap = this.mapInstance();
      if(thisMap) {
        const currentSelectedWOs = [...this.selectedWOIDs];
  
        // Create sets from the arrays for efficient lookup
        const newSelectedWOsSet = new Set(newSelectedWOs.map((item)=> item.name));
        const currentSelectedWOsSet = new Set(currentSelectedWOs.map((item)=> item.name));

        // Find unique values in newSelectedWOs
        const newSelections: WorkOrderBase[] = newSelectedWOs.filter(value => !currentSelectedWOsSet.has(value.name));
        if(newSelections.length > 0) {
          this.updatePointsOnMap(thisMap, newSelections, true); 
        }
  
        // Find unique values in currentSelectedWOs
        const removeSelections: WorkOrderBase[] = currentSelectedWOs.filter(value => !newSelectedWOsSet.has(value.name));
        if(removeSelections.length > 0) {
          this.updatePointsOnMap(thisMap, removeSelections, false); 
        }

        // to work this requires effect allowSignalWrites config to be true 
        this.pendingMapSelection.set(false);

        this.selectedWOIDs = newSelectedWOs;
      }
    });

    effect(() => { 
      if(this.selectMapTool()) {
        this._mapSharedUtilService.prevMode = this.selectMapTool();
      }
    });
    // effect to reset map whenever workOders changes
    effect(() => { 
      const workOrders = this.workOrders();
      if(workOrders.length > 0) {
        if(this.mapInstance()) {
          this.mappopupRef?.remove();
          this.randomTileID = Math.random();//set a random number to tileID to refresh data on recall,dispatch,applyfilters to avoid cache
          this.applyFilter(this.mapInstance());
        }
      }
    });

    effect(() => { 
      const gridData = this.gridData();
      const thisMap = this.mapInstance();
      if(thisMap) {
        const wOs = [...gridData.data].map((item) => item.name);
        if(thisMap.getLayer(this.pointSource)) {
          thisMap.setFilter(this.pointSource, [
            'all',
            ['<=', 'CountofWOSamePosition', 1],
            [
            'in', 
            'Name', 
            ...wOs
            ]
          ]);
        }
        if(thisMap.getLayer(this.terrainDataSquare)) {
          thisMap.setFilter(this.terrainDataSquare, [
            'all',
            ['>', 'CountofWOSamePosition', 1],
            [
            'in', 
            'Name', 
            ...wOs
            ]
          ]);
        }
      }
    });
  }

  

  ngOnInit(): void {
    this.spiderfyService.isActive = false;
    const langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.scheduleMapControlTooltipTranslation();
    });
    this.subscriptions.push(langChangeSub);

    this.fullscreenChangeHandler = () => {
      this.scheduleMapControlTooltipTranslation();
    };
    document.addEventListener('fullscreenchange', this.fullscreenChangeHandler);

    const keyDownHandler = (event) => {
      if(event.repeat)return;
      if (event.key === 'Shift') {
        this.isShiftKeyPressed.set(true);
        this._mapSharedUtilService.showModePopup.set(true);
      };
    }
    
    const keyUpHandler = (event) => {
      if(event.repeat)return;
      if (event.key === 'Shift') {
        this.isShiftKeyPressed.set(false);
        this._mapSharedUtilService.showModePopup.set(false);
      }
    }
    
    window.addEventListener('keydown', keyDownHandler);
    window.addEventListener('keyup', keyUpHandler);

  }
  applyFilter(thisMap: MaplibreMap): void {
    //TODO - Commented Hexagon Layer due to performance
    //(thisMap.getSource(this.hexagonSource) as VectorTileSource).setTiles([this.formatSourceURL('hexagon')]);
    (thisMap.getSource(this.pointSource) as VectorTileSource)?.setTiles([this.formatSourceURL('point')]);
  }

  onMapLoad(thisMap: MaplibreMap) {
    this.mapInstance.set(thisMap);
    this._mapSharedUtilService.mapInstanceSet = thisMap;/*to start the RX */
    thisMap._refreshExpiredTiles = false;//not to refresh tiles when map is not in use / if tile is expired

    // Bind functions once and store them
    this.sourcedataBound = this.sourcedata.bind(this);
    this.idleEventBound = this.idleEvent.bind(this);
    this.styleDataEventBound = this.styleDataEvent.bind(this);

    thisMap.on('sourcedata', this.sourcedataBound);

    thisMap.on('idle', this.idleEventBound);

    // Add source and layer whenever base style is loaded
    thisMap.on('styledata', this.styleDataEventBound);
  

    // add polygon tool
    this._mapSharedUtilService.addDrawControlToMap(thisMap,
      (e, features: Array<Feature>) => {
        this.polygonFeature = features;
        //only if polygon selection is changed call the selectWO method
        if (this.prevpolygonFeature != this.polygonFeature) {
          this.prevpolygonFeature = [...this.polygonFeature];
          let polygonCoords: Position[] = [];

          this.polygonFeature?.forEach((f) => {
            const [c] = (f.geometry as any).coordinates;
            if (c != null) {
              polygonCoords = c as Position[];
            }
          });

          if (polygonCoords?.[0]?.length) {
            // Error when map is loading
            if(this.loadingMap() === true) {
              this._mapSharedUtilService.removePolygon();//as a safety step remove any existing polygon drawn in map
              this.notifications.showError(this.translate.instant('HOME_COMPONENTS.WORKORDERS_MAP.PLEASE_WAIT_MAP_LOADING'));
              return;
            }
            // Refuse polygon-to-grid selection while the work order stream is
            // still in flight — selecting against a partial dataset gives the
            // user a misleading result.
            if (this._clientWO.streamingInProgress()) {
              this._mapSharedUtilService.removePolygon();
              this.notifications.showError(this.translate.instant('HOME_COMPONENTS.WORKORDERS_MAP.PLEASE_WAIT_MAP_LOADING'));
              return;
            }
            // Error when we are processing the request
            if(this.pendingMapSelection() === true) {
              this._mapSharedUtilService.removePolygon();//as a safety step remove any existing polygon drawn in map
              this.notifications.showError(this.translate.instant('HOME_COMPONENTS.WORKORDERS_MAP.PLEASE_WAIT_SELECTING_WORK_ORDERS'));
              return;
            }
            // 
            if(thisMap.areTilesLoaded()) {              
              this.pendingMapSelection.set(true);
              const data = {
                contractName: this.contractName(),
                startDate: this.startDateFilter(),
                endDate: this.endDateFilter()
              };
              const gridState:any = {
                ...this.gridState(),
                filter: {
                  logic: 'and',
                  filters: []
                }
              }

              //check if a point falls in polygon zone if so, select
              const getLayerDataByPolygonSub = this._clientWO.getLayerDataByPolygon({ type: 'Polygon', coordinates: [polygonCoords] }, data, gridState, this.columnsList(),this.installDateOption()).subscribe((WOs) => {
                const features = this.getAllFeatures(thisMap);
                // Convert externalFeatures into a Set for faster lookups
                const featureNamesSet = new Set(WOs.map(feature => feature));

                // Filter features based on the presence in the Set
                const filteredFeatures = features.filter(item => featureNamesSet.has(item.properties.Name));
                this.zone.run(() => {
                  this.selectWOinMap(filteredFeatures);  
                  this._mapSharedUtilService.removePolygon();
                });
              })
              this.subscriptions.push( getLayerDataByPolygonSub );
            } else {
              this._mapSharedUtilService.removePolygon();
            }
          }
        }
      }
    );

    this.scheduleMapControlTooltipTranslation();

  }

  private scheduleMapControlTooltipTranslation(): void {
    window.setTimeout(() => {
      this._mapSharedUtilService.updateMapControlTooltips();
    });
  }
  flattenFilterGroup(group: CompositeFilterDescriptor): CompositeFilterDescriptor {
    const flatFilters: FilterDescriptor[] = [];

    const collect = (item: FilterDescriptor | CompositeFilterDescriptor) => {
      if ('field' in item) {
        flatFilters.push(item);
      } else {
        const composite = item as CompositeFilterDescriptor;
        composite.filters.forEach(collect);
      }
    };
    group.filters.forEach(collect);
    return {
      logic: group.logic,
      filters: flatFilters
    };
  }

  styleDataEvent(e) {
     if((e as any)?.style?.stylesheet?.id === 'outdoor-v2'){//if the style is from Terrain set pitch 
      this._mapSharedUtilService.mapInstanceGet.setPitch(70);
      this._mapSharedUtilService.mapInstanceGet.setMaxPitch(85);
      this._mapSharedUtilService.mapInstanceGet.setBearing(30);
    } 
    if((e as any)?.style?.stylesheet?.id === 'openstreetmap' &&  this._mapSharedUtilService.mapInstanceGet.getSource(this.pointSource) &&
    this._mapSharedUtilService.mapInstanceGet.getSource(this.pointSource).loaded()){
    const configurations = this._config.getConfig().additionalLayers;
    const filteredConfigurations = configurations.filter((config) => !config.contractNameFilter || config.contractNameFilter === this.contractName);
    this.additionalLayers = filteredConfigurations;

    for (const config of filteredConfigurations) {
      switch (config.name) {
        case 'Satellite Imagery':
          if(config.visible){
            if(this._mapSharedUtilService.mapInstanceGet.getStyle() != config.sourceURL){
              this.additionalLayers.find(l => l.name === 'Terrain Overlays').visible = false;
              this.additionalLayers.find(l => l.name === '3DBuildings').visible = false;
            }
          }
          break;
        case 'Terrain Overlays':
          if(config.visible){
            if(this._mapSharedUtilService.mapInstanceGet.getStyle() != config.sourceURL){
              this.additionalLayers.find(l => l.name === 'Satellite Imagery').visible = false;
              this.additionalLayers.find(l => l.name === '3DBuildings').visible = false;
              this._mapSharedUtilService.mapInstanceGet.setTerrain({
                source: "terrain",
                exaggeration : 1
              });
              this.addLayers(this._mapSharedUtilService.mapInstanceGet, true)
            }

          }
          break;  
        default:
          break;
      }
    }
  }
     
  } 
  idleEvent(e) {
    const thisMap = this.mapInstance();
    if(!thisMap.getSource(this.pointSource)){
      thisMap.addSource(this.pointSource, {
        type: 'vector',
        tiles: [this.formatSourceURL('point')],
        minzoom: MapSharedUtilService.minzoom,
        promoteId: 'Name',
      });
      //remove the image and add if not it takes the image from cache and new envs it wouldnt render
      thisMap.removeImage('square')
      this.addImage(thisMap)

      this.addLayers(thisMap, true);
      this.addAdditionalLayers(thisMap);
      this.updatePointsOnMap(thisMap,this.selectedWorkOrders(), true);
    }

    // if map source changes reload the legend
    if(this.trackMapSourceChange()) {
      const pointSource = thisMap.getSource(this.pointSource);
      if (pointSource && pointSource.loaded()) {
        if(thisMap.areTilesLoaded()){
          this.loadLegend(thisMap);
        }
        // Update global state only if necessary
        const currentZoom = thisMap.getZoom();
        const currentCenter = thisMap.getCenter();
        if (MapSharedUtilService.defaultZoom !== currentZoom) {
          MapSharedUtilService.defaultZoom = currentZoom;
        }
        if (MapSharedUtilService.defaultCenter !== currentCenter) {
          MapSharedUtilService.defaultCenter = currentCenter;
        }
      }
      this.trackMapSourceChange.set(false);
    }
    this.zone.run(() => {
      this.loadingMap.set(false);
    });
  }
  sourcedata(e) {
    // check is point source is changed
    if (e.sourceId === this.pointSource) {
      this.zone.run(() => {
        this.trackMapSourceChange.set(true);
        if(this.loadingMap() === false) {
          this.loadingMap.set(true);
        }
      });
    }
  }

  public additionalLayers = [];

  async addAdditionalLayers(thisMap: maplibregl.Map) {
    const configurations = this._config.getConfig().additionalLayers;

    const filteredConfigurations = configurations.filter((config) => !config.contractNameFilter || config.contractNameFilter === this.contractName);
    this.additionalLayers = filteredConfigurations;

    for (const config of filteredConfigurations) {
      if(config.iconSize ){
        if(config.name.includes('TechnicianAddress')||config.name.includes('CGR')) {
          
        const image = await thisMap.loadImage(config.iconURL);
        if(!thisMap.getImage(`${config.name}-icon`))
          thisMap.addImage(`${config.name}-icon`, image.data);//Todo add the icon instead of the iconURL. Remove iconURL 
        if(!thisMap.getSource(`${config.name}-Source`))
          thisMap.addSource(`${config.name}-Source`, {
            type: config.sourceType,
            data: config.sourceURL,
            promoteId: `${config.name}-Name`

          });
        if(!thisMap.getLayer(`${config.name}-layer`))
          thisMap.addLayer({
            id: `${config.name}-layer`,
            type: "symbol",
            source: `${config.name}-Source`,
            layout: {
              "icon-image": `${config.name}-icon`,
              "icon-size": config.iconSize,
              'text-field': ['get', 'Tech ID'],
              "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
              "text-size" : 10,
              "text-line-height":2,
              "text-offset": [0, 0.6],
              "text-anchor": "top",
              visibility: config.visible ? 'visible' : 'none'
            }
          });
      }
      }
      switch (config.name) {
        case '3DBuildings':
            this.add3DBuildingsLayer(thisMap,config)
            break;
        case 'Spiderify':
            this.spiderifyEffect.set(config.visible);
            break;
        default:
          break;
      }
    }
      
          
    }

  add3DBuildingsLayer(thisMap,config){
    // Insert the layer beneath any symbol layer.
    const stylelayers = thisMap.getStyle().layers;
    let labelLayerId;
    for (let i = 0; i < stylelayers.length; i++) {
        if (stylelayers[i].type === 'symbol' && stylelayers[i].layout['text-field']) {
            labelLayerId = stylelayers[i].id;
            break;
        }
    }
    if(config.visible){
    //tilt the map to get a good view of 3d buildings
    thisMap.setBearing(-17.6);
    thisMap.setPitch(45);
    thisMap.setMaxPitch(60);
    }
    else{
    //default pitch
    thisMap.setBearing(-0);
    thisMap.setPitch(0);
    thisMap.setMaxPitch(60);
    }
    if(thisMap.getSource('openmaptiles')){
      thisMap.addLayer(
        {
            'id': `${config.name}-layer`,
            'source': 'openmaptiles',
            'source-layer': 'building',
            'type': 'fill-extrusion',
            'minzoom': 15,
            'paint': {
                'fill-extrusion-color': [
                    'interpolate',
                    ['linear'],
                    ['get', 'render_height'], 0, 'lightgray', 200, 'royalblue', 400, 'lightblue'
                ],
                'fill-extrusion-height': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    15,
                    0,
                    16,
                    ['get', 'render_height']
                ],
                'fill-extrusion-base': ['case',
                    ['>=', ['get', 'zoom'], 16],
                    ['get', 'render_min_height'], 0
                ]
            }
        },
        labelLayerId
      );
    }
    
  }

  toggleLayerVisibility(layerName: string) {
    const layer = this.additionalLayers.find(l => l.name === layerName);
    
    switch (layerName) {
      case '3DBuildings':
          this.additionalLayers.find(l => l.name === 'Satellite Imagery').visible = false;
          this.additionalLayers.find(l => l.name === 'Terrain Overlays').visible = false;
          
          if(layer.visible){
            
            this._mapSharedUtilService.mapInstanceGet.setStyle(this.openMapTileUrl);
            this._mapSharedUtilService.mapInstanceGet.setBearing(-17.6);this._mapSharedUtilService.mapInstanceGet.setPitch(45);
          }
          else{
            MapSharedUtilService.removeLayer(this._mapSharedUtilService.mapInstanceGet, `${layerName}-layer`);
            this._mapSharedUtilService.mapInstanceGet.setPitch(0);
            this._mapSharedUtilService.mapInstanceGet.setMaxPitch(60);
            this._mapSharedUtilService.mapInstanceGet.setBearing(-0);
          }
        break
      case 'Satellite Imagery':
          if(layer.visible){
            this.additionalLayers.find(l => l.name === 'Terrain Overlays').visible = false;
            this.additionalLayers.find(l => l.name === '3DBuildings').visible = false;
            this._mapSharedUtilService.mapInstanceGet.setStyle(layer.sourceURL);
          }
          else{
            this._mapSharedUtilService.mapInstanceGet.setStyle(this.openMapTileUrl);
          }
          break;
      case 'Terrain Overlays':
          if(layer.visible){
            
            this.additionalLayers.find(l => l.name === 'Satellite Imagery').visible = false;
            this.additionalLayers.find(l => l.name === '3DBuildings').visible = false;
            this._mapSharedUtilService.mapInstanceGet.setStyle(layer.styleURL);
            
            this._mapSharedUtilService.mapInstanceGet.addSource("terrain", {
              "type": "raster-dem",
              "url": layer.sourceURL,
              
            });
            this._mapSharedUtilService.mapInstanceGet.setTerrain({
              source: "terrain",
              exaggeration : 1
            });

          }
          else{
            
            this._mapSharedUtilService.mapInstanceGet.setPitch(0);
            this._mapSharedUtilService.mapInstanceGet.setMaxPitch(60);
            this._mapSharedUtilService.mapInstanceGet.setBearing(-0);
            this._mapSharedUtilService.mapInstanceGet.setStyle(this.openMapTileUrl);
          }
          break;
      case 'Spiderify':
          this.spiderifyEffect.set(layer.visible)
          break;
      default:
        break;
    }

    if(!layerName.includes('Spiderify') && !layerName.includes('Terrain Overlays') && !layerName.includes('Satellite Imagery') ){
      const visibility = layer.visible ? 'visible' : 'none';
      this._mapSharedUtilService.mapInstanceGet.setLayoutProperty(`${layerName}-layer`, 'visibility', visibility);
    }
  }

  updatePointsOnMap(thisMap: MaplibreMap, workOrders:WorkOrderBase[], isSelected:boolean) {
    //added zone cos, the UI is not aware of this selection so map was not highlighting WO's 
    this.zone.run(() => {

      //TO SELECT
      if(thisMap.getSource(this.pointSource)) {
        workOrders.forEach((f) => {
          thisMap.setFeatureState(
            {
              source: this.pointSource,
              sourceLayer: this.layerName,
              id: f.name,
            },
            { isSelected: isSelected }
          );
        });
      }
    })
  }

  private getAllFeatures(thisMap: MaplibreMap): MapGeoJSONFeature[] {

    const wOs = [...this.gridData().data].map((item) => item.name);
    // gridData filter to sync map features with the grid state
    return thisMap.querySourceFeatures(this.pointSource, {
      sourceLayer: this.layerName,
      filter: ['in', 'Name', ...wOs]
    }) as maplibregl.MapGeoJSONFeature[];
  }

  formatSourceURL(layer): string {
    const tileservUrl = this._config.getConfig().tileservUrl;
    const tilesLayerName = this._config.getConfig().tilesLayerName;
    const tileServeURL = tileservUrl + tilesLayerName;
    const tile = `${tileServeURL}/{z}/{x}/{y}.pbf?_query_id=${this.queryId()}&_layer=${layer}&random=${this.randomTileID}`;//refresh data on recall,dispatch,applyfilters to avoid cache
    return tile;
  }

  private URLEncodeFilterValues(filter: CompositeFilterDescriptor){
    var encodedFilter = filter ? {...filter} : { logic: "and", filters: [] };
    encodedFilter?.filters.forEach(f => {
      if(Array.isArray((f as FilterDescriptor).value)){
        var values = (f as FilterDescriptor).value.map(x => x);

        for(var i=0;i<values.length;i++){
          values[i] = encodeURIComponent(values[i]);
        }

        (f as FilterDescriptor).value = values;
      }else{
        (f as FilterDescriptor).value = encodeURIComponent((f as FilterDescriptor).value);
      }            
    });
    return encodedFilter;
  }

  //refresh Legend in Map
  public loadLegend(thisMap) {
    const features = this.getAllFeatures(thisMap);
    if (features?.length > 0) {
      //for the legend
      let fsrNamesOnMap = features
        .filter(
          (f) =>
            f.properties['FsrName'] != '' &&
            f.properties['FsrName'] != undefined
        )
        .map((f) => {
          return {
            FsrName: f.properties['FsrName'],
            FsrColour: f.properties['IconColour'],
          };
        });

        let teamsOnMap = features
        .filter(
          (f) =>
            f.properties['TeamDispatchId'] != '' &&
            f.properties['TeamDispatchId'] != undefined
        )
        .map((f) => {
          return {
            TeamId: f.properties['TeamDispatchId'],
            TeamColour: f.properties['IconColour'],
          };
        });

      const uniquefsrNamesOnMap = fsrNamesOnMap.reduce((acc, item) => {
        if (!acc.some((e) => e.FsrName == item.FsrName)) {
          acc.push(item);
        }
        return acc;
      }, []);

      const uniqueTeamsOnMap = teamsOnMap.reduce((acc, item) => {
        if (!acc.some((e) => e.TeamId == item.TeamId)) {
          acc.push(item);
        }
        return acc;
      }, []);

      this.zone.run(() => {
        this.fsrNames4LegendC = uniquefsrNamesOnMap; //to know if the names changed and call again the method, otherwise no update
        this.teams4LegendC = uniqueTeamsOnMap;
        this.ref.markForCheck();
      });
    }

  }

  //generate and add the square image
  public addImage(thisMap: MaplibreMap) {
    const id = 'square';
    const width = 5; // The image will be 64 pixels square.
    const bytesPerPixel = 4; // Each pixel is represented by 4 bytes: red, green, blue, and alpha.
    const data = new Uint8Array(width * width * bytesPerPixel);

    for (let x = 0; x < width; x++) {
      for (let y = 0; y < width; y++) {
        const offset = (y * width + x) * bytesPerPixel;
        data[offset + 0] = 255; // red
        data[offset + 1] = 209; // green
        data[offset + 2] = 28; // blue
        data[offset + 3] = 255; // alpha
      }
    }
    thisMap.addImage(
      id,
      { width: width, height: width, data: data },
      { sdf: true }
    );
  }

  removeLayers(thisMap:MaplibreMap) {
    this.eventSub.forEach((e) => {
      thisMap.off(e.name, e.layer, e.event);
    });
    this.eventSub.length = 0;

  }
  addLayers(thisMap: MaplibreMap, addEvents = true): void {
    this.removeLayers(thisMap);
    MapSharedUtilService.removeLayer(thisMap, this.pointSource);
    MapSharedUtilService.removeLayer(thisMap, this.terrainDataSquare);
    //To Add  circle pin with white(default) /Gray(RecallRequested) border for WO's
    const wOs = [...this.gridData().data].map((item) => item.name);
    thisMap.addLayer({
      id: this.pointSource,
      type: 'circle',
      minzoom: MapSharedUtilService.minzoom, // This layer will be visible from this zoom level and above
      maxzoom: MapSharedUtilService.maxzoom,
      source: this.pointSource,
      'source-layer': this.layerName,
      paint: {
        'circle-radius': 7,
        'circle-color': [
          'case',
          ["all",["!=",["get","StatusWebName"],WOStatusWeb.ReadyForDispatch],["==",["get","AssignedTo"],'']],
          '#000000',
          ['==', ['get', 'StatusWebName'], WOStatusWeb.ReadyForDispatch],
          '#' + ReadyForDispatchColor.P_888888, //default colour for ReadyforDispatch WO's is gray
          ['==', ['get', 'StatusWebName'], WOStatusWeb.UTC],
          '#' + ReadyForDispatchColor.P_888888, //default colour for UTC WO's is gray
          ['==', ['get', 'StatusWebName'], WOStatusWeb.OnHold],
          '#000000', //black colour for OnHold WO's 
          ['==', ['get', 'StatusWebName'], WOStatusWeb.ReadyForAudit],
          '#' + ReadyForDispatchColor.P_888888, //default colour for Ready For Audit WO's is gray as it has to be dispatchable 
          ['concat', '#', ['get', 'IconColour']],
        ],
        'circle-stroke-color': [
          'case',
          // Completed + selected = red
          [
            'all',
            ['==', ['get', 'StatusWebName'], WOStatusWeb.Completed],
            ['boolean', ['feature-state', 'isSelected'], false],
          ],
          'red',
          // Completed + not selected = green
          [
            'all',
            ['==', ['get', 'StatusWebName'], WOStatusWeb.Completed],
            ['!', ['boolean', ['feature-state', 'isSelected'], false]],
          ],
          '#39FF14',
          // Recall Requested = gray
          ['==', ['get', 'StatusWebName'], WOStatusWeb.RecallRequested],
          'gray',
          // Any other selected (except Recall Requested) = red
          [
            'all',
            ['boolean', ['feature-state', 'isSelected'], false],
            ['!=', ['get', 'StatusWebName'], WOStatusWeb.RecallRequested],
          ],
          'red',
          // Default
          ['concat', '#', ['get', 'TaskStatusColour']],
        ],
        'circle-stroke-width': 3,
      },
      filter: [
        'all',
        ['<=', 'CountofWOSamePosition', 1],
        [
        'in', 
        'Name', 
        ...wOs
        ]
      ]
    });

    //TODO - Commented Hexagon Layer due to performance
    // thisMap.addLayer({
    //   id: 'work_order_hexagon_flat',
    //   source: this.hexagonSource,
    //   'source-layer': 'default',
    //   maxzoom: MapSharedUtilService.minzoom,
    //   type: 'fill',
    //   paint: {
    //     'fill-opacity': 1,
    //     'fill-color': ['get', 'color']
    //   },
    // });

    // thisMap.addLayer({
    //   id: 'work_order_hexagon_flat_count',
    //   source: this.hexagonSource,
    //   'source-layer': 'default',
    //   maxzoom: MapSharedUtilService.minzoom,
    //   type: 'symbol',
    //   layout: {
    //     'text-field': ['get', 'id_cnt'],
    //     'text-font': ['Open Sans Semibold'],
    //     'text-size': 10,
    //   },
    // });

    // thisMap.addLayer({
    //   id: 'work_order_hexagon_flat_border',
    //   source: this.hexagonSource,
    //   'source-layer': 'default',
    //   maxzoom: MapSharedUtilService.minzoom,
    //   type: 'line',
    //   paint: {
    //     'line-width': 0.5,
    //     'line-color': '#646464',
    //   },
    // });

    //adding a black square image and then changing the color of the icon based on color assigned to FS
    thisMap.addLayer({
      id: this.terrainDataSquare,
      source: this.pointSource,
      'source-layer': 'default',
      type: 'symbol',
      minzoom: MapSharedUtilService.minzoom + 8,
      maxzoom: MapSharedUtilService.maxzoom,
      layout: {
        'text-field': [
          'case',
          ['>', ['get', 'CountofWOSamePosition'], 99],
          '99+', //default colour for ReadyforDispatch WO's is gray
          ['get', 'CountofWOSamePosition'],
        ],
        'text-font': ['Open Sans Semibold'],
        'text-size': 9,
        'icon-allow-overlap': true,
        'text-allow-overlap': false,
        'text-ignore-placement': true,
        'icon-size': 4,
        'icon-image': 'square',
      },
      paint: {
        "text-color": "black",
        'icon-color': [
          'case',
          ["all",["!=",["get","StatusWebName"],WOStatusWeb.ReadyForDispatch],["==",["get","AssignedTo"],'']],
          '#000000',
          ['==', ['get', 'StatusWebName'], WOStatusWeb.ReadyForDispatch],
          '#' + ReadyForDispatchColor.P_888888, //default colour for ReadyforDispatch WO's is gray
          ['==', ['get', 'StatusWebName'], WOStatusWeb.UTC],
          '#' + ReadyForDispatchColor.P_888888, //default colour for UTC WO's is gray
          ['==', ['get', 'StatusWebName'], WOStatusWeb.OnHold],
          '#000000', //black colour for OnHold WO's 
          ['concat', '#', ['get', 'IconColour']],
        ],
        'icon-halo-color': [
          'case',
          // Completed + selected = red
          [
            'all',
            ['==', ['get', 'StatusWebName'], WOStatusWeb.Completed],
            ['boolean', ['feature-state', 'isSelected'], false],
          ],
          'red',
          // Completed + not selected = green
          [
            'all',
            ['==', ['get', 'StatusWebName'], WOStatusWeb.Completed],
            ['!', ['boolean', ['feature-state', 'isSelected'], false]],
          ],
          '#39FF14',
          // Recall Requested = gray
          ['==', ['get', 'StatusWebName'], WOStatusWeb.RecallRequested],
          'gray',
          // Any other selected (except Recall Requested) = red
          [
            'all',
            ['boolean', ['feature-state', 'isSelected'], false],
            ['!=', ['get', 'StatusWebName'], WOStatusWeb.RecallRequested],
          ],
          'red',
          // Default
          ['concat', '#', ['get', 'TaskStatusColour']],
        ],
        'icon-halo-width': 23,
      },
      filter: [
        'all',
        ['>', 'CountofWOSamePosition', 1],
        [
        'in', 
        'Name', 
        ...wOs
        ]
      ]
    })

    if (addEvents) {
      //adding both circle and square layers to add events
      let layers = [this.pointSource, this.terrainDataSquare]

      layers.forEach((layer1) => {
        //Click event for Pins (circle and square)
        // Spiderfy Logic on mouse over for terrain-data-square          
        if(layer1 === this.terrainDataSquare) {
          const mouseoverEventBound = this.mouseOverEvent.bind(this);
          thisMap.on('mouseover', layer1, mouseoverEventBound);
          this.eventSub.push({ name: 'mouseover', layer: layer1, event: mouseoverEventBound});
        }

        const clickEventBound = this.onClickEvent.bind(this);
        // Click event for Pins (circle and square)
        thisMap.on('click', layer1, clickEventBound);
        this.eventSub.push({ name: 'click', layer: layer1, event: clickEventBound});

        const contextEventBound = this.onContextMenu.bind(this);
        // Right Click event for Pins
        thisMap.on('contextmenu', layer1, contextEventBound);

        this.eventSub.push({ name: 'contextmenu', layer: layer1, event: contextEventBound});
      });
    }
  }

  //select /unselect a pin in map
  selectWOinMap(WOs: MapGeoJSONFeature[], toggle = false) {
    const selectedWOIDs:WorkOrderBase[] = [...this.selectedWorkOrders()]; //use nullish coalescing operator to handle null values
    
    const workOrders:WorkOrderBase[] = this.workOrders();

    // Create a Map from arr for quick lookup by id
    const newWOMap = new Map<string, any>();
    WOs.forEach(item => {
      if(item.properties.StatusWebName !== WOStatusWeb.RecallRequested){
        newWOMap.set(item.properties.Name, item)
      }
    });
    // Filter workOrders by checking if the name exists in the Map
    const filteredWOs = workOrders.filter(item => newWOMap.has(item.name));
    const selectedWOIDsSet = new Set(selectedWOIDs.map(feature => feature.name));

    filteredWOs.forEach((item) => {
      const exists = !!selectedWOIDsSet.has(item.name);
      let isShiftKeyPressed = this.isShiftKeyPressed();//polygon/rectangle tool - if space bar pressed then unselect, if not select
      if (toggle) {//if toggle (single point tool) or shift key is not pressed
        this.toggleSelectUnSelect(selectedWOIDs,item,exists)
      } 
      else if (isShiftKeyPressed) {//if shift key is pressed then unselect
        if(exists) //if WO selected /exists then remove
          selectedWOIDs.splice(selectedWOIDs.indexOf(item), 1);
      }
      else{
        if(!exists) //if WO not selected /not exists then add
          selectedWOIDs.push(item);
      }
    });
    // update global selected woids
    this.updateWOSelection.emit(selectedWOIDs);
  }

  public toggleSelectUnSelect(selectedWOIDs: WorkOrderBase[], item: WorkOrderBase, exists: boolean) {
    if (exists) {
      //remove
      selectedWOIDs.splice(selectedWOIDs.indexOf(item), 1);
    } else {
      //add
      selectedWOIDs.push(item);
    }
    return selectedWOIDs;
  }

  public showPopup(f): void {
    let coords = (f.geometry as any).coordinates.slice();
    this._clientWO.getOne(f.properties.Name).subscribe({
      next: (wo) => {
        const thisMap = this.mapInstance();
        const count = f.properties.CountofWOSamePosition;
        const description = this.spiderfyService.getHtmlContent(wo, count);
        this.mappopupRef?.remove();
        this.mappopupRef = new maplibregl.Popup()
          .setLngLat(coords)
          .setHTML(description)
          .addTo(thisMap);
      },
      error: (error) => {
        this.notifications.showError(
          this.translate.instant('HOME_COMPONENTS.WORKORDERS_MAP.POPUP_LOAD_ERROR')
        );
      }
    });
  }

  ngOnDestroy() {
    const thisMap = this.mapInstance();
    if(thisMap){
      // Properly remove event listeners using stored references
      thisMap.off('sourcedata', this.sourcedataBound);
      this.sourcedataBound = null;
      thisMap.off('idle', this.idleEventBound);
      this.idleEventBound = null;
      thisMap.off('styledata', this.styleDataEventBound);
      this.styleDataEventBound = null;
      // all the events linked to layers.
      this.removeLayers(thisMap);
      this._mapSharedUtilService.clearEventsSubscriptions(thisMap);
      this._mapSharedUtilService.mapInstanceSet = null;
    }

    this.subscriptions.forEach(sub => sub.unsubscribe());
    if (this.fullscreenChangeHandler) {
      document.removeEventListener('fullscreenchange', this.fullscreenChangeHandler);
      this.fullscreenChangeHandler = null;
    }
    window.removeAllListeners('keydown');
    window.removeAllListeners('keyup');
  }

  trackByFn(index, item) {
    return item.contractNameFilter; // or unique identifier
  }

  getAdditionalLayerTranslationKey(layerName: string): string {
    return this.additionalLayerTranslationKeys[layerName] ?? layerName;
  }

  activateSpiderfy(f, tileFeatures) {
    // get feature coordinates;
    const latLng = (f.geometry as any).coordinates.slice();
    tileFeatures = tileFeatures.filter((element) => {
      const elementLatLng = (element.geometry as any).coordinates.slice();
      return !!(latLng[0] === elementLatLng[0] && latLng[1] === elementLatLng[1]);
    });

    const features = tileFeatures.map((tileFeature) => {
      const iconColour = tileFeature.properties && tileFeature.properties.IconColour ? tileFeature.properties.IconColour : 'grey';
      const taskStatusColour = tileFeature.properties && tileFeature.properties?.TaskStatusColour ? tileFeature.properties.TaskStatusColour : '#ffffff';
      const name = tileFeature.properties.Name;
      const feature = this.workOrdersMap().get(name);
      return {
        ...feature,
        iconColour,
        taskStatusColour
      }
    });
    // generate spiderfy effect
    this.spiderfyService.spiderfy(latLng, features);
  }

  eventSub:any[] = [];
  onContextMenu(e) {
    let [f] = e.features;
    // 60084 - If spiderfyService is active don't show the work order.
    if(this.spiderfyService.isActive === false) {
      this.showPopup(f);
    }
  }
  onClickEvent(e) {
    const thisMap = this.mapInstance();
    //select/unselect only if the marker selection tool icon is clicked
    if (this._mapSharedUtilService.singlePointSelection) {

      // Error when map is loading
      if(this.loadingMap() === true) {
        this.notifications.showError(this.translate.instant('HOME_COMPONENTS.WORKORDERS_MAP.PLEASE_WAIT_MAP_LOADING'));
        return;
      }

      //#region to avoid double click, i tried e.stopPropagation(); and rxjs.debounceTime(300) and none work, so did it manually
      // If spiderfyService is active don't select the work order. - 
      // 60086 - If spiderfyService is active don't select the work order underneath spiderfy.
      if (!this.isClickAllowed || this.spiderfyService.isActive) { return; }
      this.isClickAllowed = false;
      setTimeout(() => (this.isClickAllowed = true), 300); // 300 ms debounce
      //#endregion


      // Error when we are processing the WO selection request
      if(this.pendingMapSelection() === true) {
        this.notifications.showError(this.translate.instant('HOME_COMPONENTS.WORKORDERS_MAP.PLEASE_WAIT_SELECTING_WORK_ORDERS'));
        return;
      }
      if(thisMap.areTilesLoaded()) {
        this.pendingMapSelection.set(true);
      }

      const features = thisMap.queryRenderedFeatures(e.point);
      const filteredArray = [];
      features.forEach(x => {

        if (x.source == 'work_order') {
          if (!filteredArray.some(y => (JSON.stringify(y) === JSON.stringify(x) ))) {
            filteredArray.push(x)
          }
        }
      })

      this.selectWOinMap(filteredArray,true);  
    }
  }
  mouseOverEvent(e) {
    // Spiderfy Logic on mouse over for terrain-data-square          
    // Turn On spiderify only if it is turned on
    // Check if spiderfyService is not active if active do not procedd
    if(this.spiderifyEffect() && this.spiderfyService.isActive === false) {
      const thisMap = this.mapInstance();
      const tileFeatures = this.getAllFeatures(thisMap).filter((item) => item.properties.CountofWOSamePosition > 1);
      const [f] = e.features; // get first feature;
      this.activateSpiderfy(f, tileFeatures);
    }
  }
}
